import React, { forwardRef } from "react";
import { Link } from "react-router-dom";
import cn from "./cn";

const VARIANTS = {
  primary:
    "bg-brand-700 text-white shadow-sm hover:bg-brand-800 focus-visible:ring-brand-300 disabled:bg-brand-300",
  secondary:
    "border border-slate-300 bg-white text-slate-800 shadow-sm hover:bg-slate-50 focus-visible:ring-brand-200 disabled:text-slate-400",
  ghost:
    "text-slate-700 hover:bg-slate-100 focus-visible:ring-brand-200 disabled:text-slate-400",
  danger:
    "bg-rose-600 text-white shadow-sm hover:bg-rose-700 focus-visible:ring-rose-300 disabled:bg-rose-300",
  dangerOutline:
    "border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 focus-visible:ring-rose-200 disabled:text-rose-300",
  success:
    "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus-visible:ring-emerald-300 disabled:bg-emerald-300",
  link: "text-brand-700 underline-offset-4 hover:underline focus-visible:ring-brand-200 px-0",
};

const SIZES = {
  sm: "h-8 px-3 text-xs gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-11 px-5 text-sm gap-2",
  icon: "h-10 w-10 p-0",
  iconSm: "h-8 w-8 p-0",
};

export const buttonClassName = ({ variant = "primary", size = "md", className } = {}) =>
  cn(
    "inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-lg font-semibold transition focus:outline-none focus-visible:ring-4 disabled:cursor-not-allowed",
    VARIANTS[variant] || VARIANTS.primary,
    SIZES[size] || SIZES.md,
    className,
  );

// Buttons default to type="button" so a control inside a <form> never submits
// it by accident; pass type="submit" explicitly for the submit action.
const Button = forwardRef(
  (
    {
      variant = "primary",
      size = "md",
      type = "button",
      loading = false,
      disabled = false,
      icon: Icon,
      iconEnd: IconEnd,
      className,
      children,
      to,
      href,
      ...props
    },
    ref,
  ) => {
    const classes = buttonClassName({ variant, size, className });
    const content = (
      <>
        {loading ? (
          <span className="ui-spinner h-4 w-4" aria-hidden="true" />
        ) : Icon ? (
          <Icon size={size === "sm" || size === "iconSm" ? 15 : 18} aria-hidden="true" />
        ) : null}
        {children}
        {IconEnd ? <IconEnd size={16} aria-hidden="true" /> : null}
      </>
    );

    if (to) {
      return (
        <Link ref={ref} to={to} className={classes} aria-disabled={disabled || undefined} {...props}>
          {content}
        </Link>
      );
    }

    if (href) {
      return (
        <a ref={ref} href={href} className={classes} {...props}>
          {content}
        </a>
      );
    }

    return (
      <button
        ref={ref}
        type={type}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {content}
      </button>
    );
  },
);

Button.displayName = "Button";

export const IconButton = forwardRef(
  ({ label, size = "icon", variant = "ghost", className, children, ...props }, ref) => (
    <Button
      ref={ref}
      size={size}
      variant={variant}
      aria-label={label}
      title={label}
      className={cn("rounded-lg", className)}
      {...props}
    >
      {children}
    </Button>
  ),
);

IconButton.displayName = "IconButton";

export default Button;
